import {
  BoltIcon,
  BuildingOfficeIcon,
  ChartBarSquareIcon,
  ClipboardDocumentListIcon,
  CubeIcon,
  FaceSmileIcon,
  HeartIcon,
  QuestionMarkCircleIcon,
  StarIcon,
  TagIcon,
  IdentificationIcon,
} from '@heroicons/react/24/outline';
import type { SystemModule } from '../../types/UserAccessContext';
import {
  SIDEBAR_DEPARTMENT_DEFINITIONS,
  SIDEBAR_OTHERS_DEPARTMENT_ICON,
} from './sidebarDepartmentDefinitions';
import { resolveDashboardAreaRoute } from './dashboardAreaRoutes';
import {
  resolveModuleMenuIcon,
  resolveModuleMenuLabel,
  TECHNOLOGY_AI_GROUP_ICON,
  TECHNOLOGY_GOVERNANCE_GROUP_ICON,
  TECHNOLOGY_SECURITY_GROUP_ICON,
} from './resolveModuleMenuIcon';
import type { SidebarDepartment, SidebarIcon, SidebarNavLeaf } from './sidebarMenuTypes';

const PEOPLE_NOTES_CODE = 'people.notes';
const PEOPLE_MY_TEAM_CODE = 'people.my-team';
const PEOPLE_COMPETENCIES_CODE = 'people.competencies';
const CUSTOMER_SUCCESS_ENGAGEMENT_CODE = 'customer-success.engagement';
const CUSTOMER_SUCCESS_HEALTH_CODE = 'customer-success.health';
const CORE_COMPANIES_CODE = 'core.companies';
const CORE_CONTACTS_CODE = 'core.contacts';
const COMMERCIAL_CONTRACTS_CODE = 'commercial.contracts';
const COMMERCIAL_PROJECTS_CODE = 'commercial.projects';

const CLIENTS_NESTED_CODES = new Set([
  CORE_COMPANIES_CODE,
  CORE_CONTACTS_CODE,
  COMMERCIAL_CONTRACTS_CODE,
  COMMERCIAL_PROJECTS_CODE,
]);

const TECHNOLOGY_SECURITY_CODES = [
  'technology.secrets',
  'technology.certificates',
  'technology.keys',
];

const TECHNOLOGY_AI_CODES = [
  'technology.ai-models',
  'technology.ai-prompts',
  'technology.ai-benchmarks',
  'technology.ai-costs',
];

const TECHNOLOGY_GOVERNANCE_CODES = [
  'technology.documentation',
  'technology.deployments',
  'technology.backups',
  'technology.monitoring',
];

const MANUAL_CODE = 'manual';
const MANUAL_CHILD_CODES = ['manual.expired', 'manual.views'] as const;

const DOCUMENTATION_CODE = 'technology.documentation';
const DOCUMENTATION_CHILD_CODES = [
  'technology.documentation-expired',
  'technology.documentation-views',
] as const;

const DOCUMENTATION_CHILD_FALLBACKS: Record<
  (typeof DOCUMENTATION_CHILD_CODES)[number],
  { routePath: string; name: string }
> = {
  'technology.documentation-expired': {
    routePath: '/technology/documentation/expired',
    name: 'Documentos expirados',
  },
  'technology.documentation-views': {
    routePath: '/technology/documentation/views',
    name: 'Visualizações',
  },
};

const TECHNOLOGY_SECURITY_CODE_SET = new Set(TECHNOLOGY_SECURITY_CODES);
const TECHNOLOGY_AI_CODE_SET = new Set(TECHNOLOGY_AI_CODES);
const TECHNOLOGY_GOVERNANCE_CODE_SET = new Set(TECHNOLOGY_GOVERNANCE_CODES);
const DOCUMENTATION_CHILD_CODE_SET = new Set<string>(DOCUMENTATION_CHILD_CODES);
const TECHNOLOGY_NESTED_CODES = new Set([
  ...TECHNOLOGY_SECURITY_CODES,
  ...TECHNOLOGY_AI_CODES,
  ...TECHNOLOGY_GOVERNANCE_CODES,
  ...DOCUMENTATION_CHILD_CODES,
]);

const REPORTS_CODES = ['reports.access'] as const;
const REPORTS_CODE_SET = new Set<string>(REPORTS_CODES);

function toNavLeaf(module: SystemModule): SidebarNavLeaf {
  return {
    code: module.code,
    to: resolveDashboardAreaRoute(module.code, module.routePath),
    label: resolveModuleMenuLabel(module.code, module.name),
    icon: resolveModuleMenuIcon(module.code),
  };
}

function buildCustomerSuccessReportsItem(
  modulesByCode: Map<string, SystemModule>,
  assignedCodes: Set<string>,
): SidebarNavLeaf | null {
  const health = modulesByCode.get(CUSTOMER_SUCCESS_HEALTH_CODE);
  if (!health) {
    return null;
  }

  assignedCodes.add(CUSTOMER_SUCCESS_HEALTH_CODE);

  const healthLeaf = toNavLeaf(health);
  const children: SidebarNavLeaf[] = [
    {
      ...healthLeaf,
      label: 'Saúde da Conta',
      icon: HeartIcon,
    },
    {
      code: 'customer-success.csat-report',
      to: '/customer-success/csat-report',
      label: 'CSAT',
      icon: StarIcon,
    },
    {
      code: 'customer-success.nps-report',
      to: '/customer-success/nps-report',
      label: 'NPS',
      icon: FaceSmileIcon,
    },
  ];

  return {
    code: 'customer-success.reports',
    to: healthLeaf.to,
    label: 'Relatórios',
    icon: ChartBarSquareIcon,
    children,
  };
}

function buildManualMenuItem(
  modulesByCode: Map<string, SystemModule>,
  assignedCodes: Set<string>,
): SidebarNavLeaf | null {
  const manual = modulesByCode.get(MANUAL_CODE);
  if (!manual) {
    return null;
  }

  assignedCodes.add(MANUAL_CODE);
  const children: SidebarNavLeaf[] = [];
  for (const code of MANUAL_CHILD_CODES) {
    const module = modulesByCode.get(code);
    if (!module) {
      continue;
    }

    assignedCodes.add(code);
    children.push(toNavLeaf(module));
  }

  return {
    ...toNavLeaf(manual),
    children: children.length > 0 ? children : undefined,
  };
}

function buildDocumentationMenuItem(
  modulesByCode: Map<string, SystemModule>,
  assignedCodes: Set<string>,
): SidebarNavLeaf | null {
  const documentation = modulesByCode.get(DOCUMENTATION_CODE);
  if (!documentation) {
    return null;
  }

  assignedCodes.add(DOCUMENTATION_CODE);

  const children: SidebarNavLeaf[] = [];
  for (const code of DOCUMENTATION_CHILD_CODES) {
    const module = modulesByCode.get(code);
    if (module) {
      assignedCodes.add(code);
      children.push(toNavLeaf(module));
      continue;
    }

    // Relatórios subordinados: aparecem com o módulo Documentação mesmo se o
    // módulo filho ainda não estiver no contexto de acesso (seed/migração).
    const fallback = DOCUMENTATION_CHILD_FALLBACKS[code];
    assignedCodes.add(code);
    children.push({
      code,
      to: resolveDashboardAreaRoute(code, fallback.routePath),
      label: resolveModuleMenuLabel(code, fallback.name),
      icon: resolveModuleMenuIcon(code),
    });
  }

  return {
    ...toNavLeaf(documentation),
    children,
  };
}

function buildDeploymentsMenuItem(
  modulesByCode: Map<string, SystemModule>,
  assignedCodes: Set<string>,
): SidebarNavLeaf | null {
  const deployments = modulesByCode.get('technology.deployments');
  if (!deployments) {
    return null;
  }

  assignedCodes.add('technology.deployments');

  return {
    ...toNavLeaf(deployments),
    children: [
      {
        code: 'technology.deployments.documentation',
        to: '/technology/deployments/documentation',
        label: 'Releases',
        icon: resolveModuleMenuIcon('technology.deployments.documentation'),
      },
    ],
  };
}

function buildGovernanceGroupItem(
  modulesByCode: Map<string, SystemModule>,
  assignedCodes: Set<string>,
): SidebarNavLeaf | null {
  const children: SidebarNavLeaf[] = [];

  for (const code of TECHNOLOGY_GOVERNANCE_CODES) {
    if (code === DOCUMENTATION_CODE) {
      const documentationItem = buildDocumentationMenuItem(modulesByCode, assignedCodes);
      if (documentationItem) {
        children.push(documentationItem);
      }
      continue;
    }

    if (code === 'technology.deployments') {
      const deploymentsItem = buildDeploymentsMenuItem(modulesByCode, assignedCodes);
      if (deploymentsItem) {
        children.push(deploymentsItem);
      }
      continue;
    }

    const module = modulesByCode.get(code);
    if (!module) {
      continue;
    }

    assignedCodes.add(code);
    children.push(toNavLeaf(module));
  }

  if (children.length === 0) {
    return null;
  }

  return {
    code: 'technology.governance',
    to: children[0].to,
    label: 'Governança',
    icon: TECHNOLOGY_GOVERNANCE_GROUP_ICON,
    children,
  };
}

function buildNestedGroupItem(
  groupCode: string,
  label: string,
  icon: SidebarIcon,
  childCodes: readonly string[],
  modulesByCode: Map<string, SystemModule>,
  assignedCodes: Set<string>,
): SidebarNavLeaf | null {
  const children: SidebarNavLeaf[] = [];

  for (const code of childCodes) {
    const module = modulesByCode.get(code);
    if (!module) {
      continue;
    }

    assignedCodes.add(code);
    children.push(toNavLeaf(module));
  }

  if (children.length === 0) {
    return null;
  }

  return {
    code: groupCode,
    to: children[0].to,
    label,
    icon,
    children,
  };
}

function buildClientsMenuItem(
  modulesByCode: Map<string, SystemModule>,
  assignedCodes: Set<string>,
): SidebarNavLeaf | null {
  const companies = modulesByCode.get(CORE_COMPANIES_CODE);
  const contacts = modulesByCode.get(CORE_CONTACTS_CODE);
  const contracts = modulesByCode.get(COMMERCIAL_CONTRACTS_CODE);
  const projects = modulesByCode.get(COMMERCIAL_PROJECTS_CODE);

  if (!companies && !contacts && !contracts && !projects) {
    return null;
  }

  const children: SidebarNavLeaf[] = [];
  if (contacts) {
    assignedCodes.add(CORE_CONTACTS_CODE);
    children.push(toNavLeaf(contacts));
  }
  if (contracts) {
    assignedCodes.add(COMMERCIAL_CONTRACTS_CODE);
    children.push(toNavLeaf(contracts));
  }
  if (projects) {
    assignedCodes.add(COMMERCIAL_PROJECTS_CODE);
    children.push(toNavLeaf(projects));
  }

  if (companies) {
    assignedCodes.add(CORE_COMPANIES_CODE);
    return {
      ...toNavLeaf(companies),
      label: resolveModuleMenuLabel(CORE_COMPANIES_CODE, companies.name),
      children: children.length > 0 ? children : undefined,
    };
  }

  const firstChild = children[0];
  return {
    code: 'core.clients',
    to: firstChild.to,
    label: 'Clientes',
    icon: BuildingOfficeIcon,
    children,
  };
}

export function buildSidebarDepartments(
  modules: SystemModule[],
  roleCodes: string[] = [],
  isOwnerCustomer = false,
): SidebarDepartment[] {
  const homeDashboardModules = new Set(['dashboard.executive', 'dashboard.prospection']);
  const usableModules = modules.filter((module) => {
    if (homeDashboardModules.has(module.code)) {
      return false;
    }

    if (
      !isOwnerCustomer &&
      (module.code === 'manual.expired' || module.code === 'manual.views')
    ) {
      return false;
    }

    return true;
  });
  const modulesByCode = new Map(usableModules.map((module) => [module.code, module]));
  const assignedCodes = new Set<string>();
  const normalizedRoles = roleCodes.map((code) => code.toLowerCase());
  const showPeopleNotesMenu =
    normalizedRoles.includes('admin') || normalizedRoles.includes('gerente');

  const departments: SidebarDepartment[] = [];

  for (const definition of SIDEBAR_DEPARTMENT_DEFINITIONS) {
    const items: SidebarNavLeaf[] = [];
    let securityGroupAdded = false;
    let aiGroupAdded = false;
    let governanceGroupAdded = false;
    let reportsGroupAdded = false;
    let customerSuccessReportsAdded = false;

    for (const code of definition.moduleCodes) {
      if (code === MANUAL_CODE || code === 'manual.expired' || code === 'manual.views') {
        if (!items.some((item) => item.code === MANUAL_CODE)) {
          const manualItem = buildManualMenuItem(modulesByCode, assignedCodes);
          if (manualItem) {
            items.push(manualItem);
          }
        }
        continue;
      }

      if (CLIENTS_NESTED_CODES.has(code)) {
        const alreadyAdded = items.some(
          (item) => item.code === CORE_COMPANIES_CODE || item.code === 'core.clients',
        );
        if (!alreadyAdded) {
          const clientsItem = buildClientsMenuItem(modulesByCode, assignedCodes);
          if (clientsItem) {
            items.push(clientsItem);
          }
        }
        continue;
      }

      if (code === CUSTOMER_SUCCESS_HEALTH_CODE) {
        if (!customerSuccessReportsAdded) {
          const reportsItem = buildCustomerSuccessReportsItem(modulesByCode, assignedCodes);
          if (reportsItem) {
            items.push(reportsItem);
          }
          customerSuccessReportsAdded = true;
        }
        continue;
      }

      if (REPORTS_CODE_SET.has(code)) {
        if (!reportsGroupAdded) {
          const group = buildNestedGroupItem(
            'reports',
            'Relatórios',
            ChartBarSquareIcon,
            REPORTS_CODES,
            modulesByCode,
            assignedCodes,
          );
          if (group) {
            items.push(group);
          }
          reportsGroupAdded = true;
        }
        continue;
      }

      if (TECHNOLOGY_NESTED_CODES.has(code)) {
        if (TECHNOLOGY_SECURITY_CODE_SET.has(code)) {
          if (!securityGroupAdded) {
            const group = buildNestedGroupItem(
              'technology.security',
              'Segurança',
              TECHNOLOGY_SECURITY_GROUP_ICON,
              TECHNOLOGY_SECURITY_CODES,
              modulesByCode,
              assignedCodes,
            );
            if (group) {
              items.push(group);
            }
            securityGroupAdded = true;
          }
          continue;
        }

        if (TECHNOLOGY_AI_CODE_SET.has(code)) {
          if (!aiGroupAdded) {
            const group = buildNestedGroupItem(
              'technology.ai',
              'IA',
              TECHNOLOGY_AI_GROUP_ICON,
              TECHNOLOGY_AI_CODES,
              modulesByCode,
              assignedCodes,
            );
            if (group) {
              items.push(group);
            }
            aiGroupAdded = true;
          }
          continue;
        }

        if (TECHNOLOGY_GOVERNANCE_CODE_SET.has(code) || DOCUMENTATION_CHILD_CODE_SET.has(code)) {
          if (!governanceGroupAdded) {
            const group = buildGovernanceGroupItem(modulesByCode, assignedCodes);
            if (group) {
              items.push(group);
            }
            governanceGroupAdded = true;
          }
          continue;
        }
      }

      if (code === 'sales.offer') {
        if (modulesByCode.has('sales.products')) {
          items.push({
            code: 'sales.offer',
            to: '/sales/offer',
            label: 'Oferta',
            icon: TagIcon,
          });
        }
        continue;
      }

      if (definition.id === 'produtos' && (code === 'products.workspace' || code === 'sales.products')) {
        if (!items.some((item) => item.code === 'products.home')) {
          const workspace = modulesByCode.get('products.workspace');
          const catalog = modulesByCode.get('sales.products');
          if (workspace || catalog) {
            if (workspace) {
              assignedCodes.add('products.workspace');
            }
            if (catalog) {
              assignedCodes.add('sales.products');
            }
            items.push({
              code: 'products.home',
              to: workspace ? '/products' : '/sales/products',
              label: 'Produtos',
              icon: CubeIcon,
            });
          }
        }
        continue;
      }

      const module = modulesByCode.get(code);
      if (!module) {
        continue;
      }

      assignedCodes.add(code);
      items.push(toNavLeaf(module));

      if (code === 'sales.prospects') {
        items.push({
          code: 'sales.leads',
          to: '/sales/leads',
          label: 'Leads',
          icon: IdentificationIcon,
        });
      }

      if (code === PEOPLE_COMPETENCIES_CODE) {
        items.push({
          code: 'people.competencies.list',
          to: '/people/competencies',
          label: 'Competências',
          icon: ClipboardDocumentListIcon,
        });
        items.push({
          code: 'people.competencies.questions',
          to: '/people/competency-questions',
          label: 'Perguntas',
          icon: QuestionMarkCircleIcon,
        });
      }

      if (code === CUSTOMER_SUCCESS_ENGAGEMENT_CODE) {
        items.push({
          code: 'customer-success.survey-forms',
          to: '/customer-success/survey-forms',
          label: 'Formulários de pesquisa',
          icon: ClipboardDocumentListIcon,
        });
        items.push({
          code: 'customer-success.automations',
          to: '/customer-success/automations',
          label: 'Automações',
          icon: BoltIcon,
        });
      }
    }

    // Admin (RH) e gerente: Anotações em Pessoas (colaboradores usam Conta → Desempenho).
    if (definition.id === 'pessoas' && showPeopleNotesMenu) {
      const notesModule = modulesByCode.get(PEOPLE_NOTES_CODE);
      if (notesModule && !items.some((item) => item.code === PEOPLE_NOTES_CODE)) {
        assignedCodes.add(PEOPLE_NOTES_CODE);
        items.push(toNavLeaf(notesModule));
      }
    }

    if (items.length === 0) {
      continue;
    }

    departments.push({
      id: definition.id,
      label: definition.label,
      icon: definition.icon,
      items,
    });
  }

  const orphanItems: SidebarNavLeaf[] = usableModules
    .filter(
      (module) =>
        !assignedCodes.has(module.code) &&
        module.code !== PEOPLE_NOTES_CODE &&
        module.code !== PEOPLE_MY_TEAM_CODE,
    )
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((module) => toNavLeaf(module));

  if (orphanItems.length > 0) {
    departments.push({
      id: 'outros',
      label: 'Outros',
      icon: SIDEBAR_OTHERS_DEPARTMENT_ICON,
      items: orphanItems,
    });
  }

  return departments;
}
